/**
 * The camera preview, like Unity's Camera Preview: with one camera selected,
 * a small live picture of what it sees sits in the viewport's corner. These
 * are its pure parts (whether there's a preview, what it shows, where it
 * goes); CameraPreviewController pushes them to the renderer and the overlay.
 */
import type { Mat4 } from "../../core/math/mat4";
import type { CameraDesc } from "../../render/proxies";
import { enabledComponent } from "../../world/entity";
import type { EntityId } from "../../world/entity";
import type { WorldReader } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import { describeView, lookingThrough } from "./cameraMath";
import type { ViewCamera } from "./cameraMath";

/** A rectangle in viewport CSS pixels, from the viewport's top left. */
export interface PreviewRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface CameraPreview {
  readonly entity: EntityId;
  /** The camera's name, which the preview's frame shows. */
  readonly name: string;
  readonly camera: CameraDesc;
  readonly rect: PreviewRect;
}

export interface PreviewInput {
  readonly world: WorldReader;
  readonly worldMatrixOf: (id: EntityId) => Mat4;
  readonly selection: SelectionState;
  readonly gameView: boolean;
  /** The camera the viewport is piloting, which needs no preview: the viewport shows it. */
  readonly piloting: EntityId | null;
  /** The viewport's own view, whose clip planes the preview keeps. */
  readonly viewport: ViewCamera;
  readonly viewportSize: { readonly width: number; readonly height: number };
}

/** Clear of the viewport's edges, in CSS pixels. */
const MARGIN = 12;
/** The preview's share of the viewport's width, within a usable range. */
const WIDTH_FRACTION = 0.26;
const MIN_WIDTH = 160;
const MAX_WIDTH = 320;

/**
 * Bottom right, in the viewport's own shape: the preview shows what looking
 * through the camera would, and that takes the viewport's aspect.
 */
export function previewRect(width: number, height: number): PreviewRect {
  const previewWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width * WIDTH_FRACTION));
  const previewHeight = previewWidth * (height / width);
  return {
    x: width - MARGIN - previewWidth,
    y: height - MARGIN - previewHeight,
    width: previewWidth,
    height: previewHeight,
  };
}

/**
 * The preview for the selection: a lone selected camera that's turned on,
 * outside Game View (which shows the level, not the editor's aids), and not
 * the one being piloted.
 */
export function cameraPreviewFor(input: PreviewInput): CameraPreview | null {
  const { selection, world } = input;
  if (input.gameView || selection.ids.length !== 1 || selection.active === null) return null;

  const entity = world.get(selection.active);
  const camera = entity && enabledComponent(entity, "Camera");
  if (!entity || !camera) return null;
  if (entity.id === input.piloting) return null;

  const view = lookingThrough(input.viewport, input.worldMatrixOf(entity.id), camera.fov as number);
  const { width, height } = input.viewportSize;
  return {
    entity: entity.id,
    name: entity.name,
    camera: describeView(view),
    rect: previewRect(width, height),
  };
}
